import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import api from '../api/axios';

// --- MUI Imports ---
import {
    Container, Box, TextField, Button, Typography, Paper, Alert, Link,
    FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormGroup, Checkbox, CircularProgress,
    createTheme, ThemeProvider
} from '@mui/material';

// --- Custom Component Imports ---
import PageAnimation from '../components/animations/PageAnimation';
import Stepper, { Step } from '../components/Stepper'; // Assuming stepper is in components/stepper/
import SEO from '../components/SEO';

// Create a theme to override the default MUI palette
const theme = createTheme({
    palette: {
        primary: {
            main: '#75a84b',
        },
    },
});

function RegisterPage() {
    // --- State Management ---
    const [isMobile, setIsMobile] = useState(false);
    const [formData, setFormData] = useState({
        // Core Account Info
        businessName: '',
        email: '',
        password: '',
        contactPerson: '',
        phoneNumber: '',
        companyWebsite: '',
        businessAddress: '',
        // Business Details
        businessType: '',
        yearsOperating: '',
        numLocations: '1',
        estimatedVolume: '',
        // Logistics
        preferredDeliveryTimes: '',
        hasLoadingDock: '',
        specialDeliveryInstructions: '',
        // Service Needs
        priorities: {
            pricing: false,
            freshness: true,
            reliability: false,
            variety: false,
            customerService: false,
        },
        minQuantityRequirements: '',
        reasonForSwitch: '',
        // Billing
        paymentMethod: '',
        statement_duration: '', // Added statement_duration
        // Final Notes
        additionalNotes: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    // Added state for phone validation error
    const [phoneError, setPhoneError] = useState('');

    // --- Handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Phone validation logic
        if (name === 'phoneNumber') {
            // Simple check: must start with + and be of reasonable length (e.g., +123456 is 7 chars minimum)
            if (value && (!value.startsWith('+') || value.length < 8)) {
                setPhoneError('Please enter a valid mobile number starting with country code (e.g., +1...)');
            } else {
                setPhoneError('');
            }
        }
    };

    const handleCheckboxChange = (e) => {
        setFormData({
            ...formData,
            priorities: {
                ...formData.priorities,
                [e.target.name]: e.target.checked,
            },
        });
    };

    useEffect(() => {
        const checkIsMobile = () => {
            const mobileBreakpoint = 768; // Common breakpoint for tablets and below
            setIsMobile(window.innerWidth < mobileBreakpoint);
        };
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    const handleFinalSubmit = async () => {
        // Prevent submit if there's a phone error
        if (phoneError) {
            setError('Please fix the errors in the form before submitting.');
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);
        try {
            // Map 'businessName' to 'companyName' if the API expects it
            const submissionData = { ...formData, companyName: formData.businessName };
            const response = await api.post('/register', submissionData);
            setSuccess(response.data.msg + " We'll review your application and get back to you soon!");
        } catch (err) {
            setError(err.response?.data?.msg || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // --- Render Logic ---
    if (success) {
        return (
            <ThemeProvider theme={theme}>
                <PageAnimation>
                    <Container maxWidth="sm">
                        <Paper elevation={6} sx={{ mt: 8, p: 4, borderRadius: 2, textAlign: 'center' }}>
                            <Typography component="h1" variant="h4" gutterBottom>
                                Application Submitted!
                            </Typography>
                            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>
                            <Button component={RouterLink} to="/login" variant="contained" sx={{ mt: 2 }}>
                                Proceed to Sign In
                            </Button>
                        </Paper>
                    </Container>
                </PageAnimation>
            </ThemeProvider>
        );
    }
    if (isMobile) {

        return (
            <ThemeProvider theme={theme}>
                <PageAnimation>
                    <SEO
                        title="Register | Apply for Wholesale Account | Matax Express"
                        description="Apply for a Matax Express wholesale account. Join Toronto's trusted B2B fresh produce supplier. Quick approval for restaurants and businesses."
                        keywords="wholesale account registration, B2B grocery Toronto, restaurant supplier application, wholesale produce sign up"
                        url="https://mataxexpress.ca/register"
                    />
                    {/* CHANGE 1: Increased container width from 'md' to 'lg' to give the stepper more space. */}
                    <Container maxWidth="lg">
                        <Paper elevation={6} sx={{ mt: 8, p: 4, borderRadius: 2 }}>
                            <Typography component="h1" variant="h4" align="center" gutterBottom>
                                📝 Matax Express Client Onboarding
                            </Typography>
                            <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
                                Complete the steps below to apply for your wholesale account.
                            </Typography>

                            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 5 }}>
                                    <CircularProgress color="primary" />
                                    <Typography variant="h6" sx={{ ml: 2 }}>Submitting your application...</Typography>
                                </Box>
                            ) : (
                                <Stepper
                                    onFinalStepCompleted={handleFinalSubmit}
                                    backButtonText="Back"
                                    nextButtonText="Next"
                                    finalStepButtonText="Submit Application"
                                >
                                    <Step>
                                        <Typography variant="h5" gutterBottom>Company Information</Typography>
                                        <TextField margin="normal" required fullWidth label="Business Name" name="businessName" value={formData.businessName} onChange={handleChange} autoFocus />
                                        <TextField margin="normal" required fullWidth label="Your Name" name="contactPerson" value={formData.contactPerson} onChange={handleChange} />
                                        <TextField margin="normal" required fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} />
                                        <TextField margin="normal" required fullWidth label="Create a Password" name="password" type="password" value={formData.password} onChange={handleChange} />
                                        <TextField
                                            margin="normal"
                                            required
                                            fullWidth
                                            label="Phone Number (e.g. +1234567890)"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={handleChange}
                                            error={!!phoneError}
                                            helperText={phoneError}
                                        />
                                        <TextField margin="normal" fullWidth label="Company Website (Optional)" name="companyWebsite" value={formData.companyWebsite} onChange={handleChange} />
                                        <TextField margin="normal" required fullWidth multiline rows={2} label="Business Address" name="businessAddress" value={formData.businessAddress} onChange={handleChange} />
                                    </Step>
                                    <Step>
                                        <Typography variant="h5" gutterBottom>Business Type & Operations</Typography>
                                        <TextField margin="normal" fullWidth label="What type of business are you?" placeholder="e.g., Restaurant, Catering Company, Grocer" name="businessType" value={formData.businessType} onChange={handleChange} />
                                        <TextField margin="normal" required fullWidth label="How long has your business been operating?" name="yearsOperating" value={formData.yearsOperating} onChange={handleChange} />
                                        <TextField margin="normal" fullWidth label="Number of locations" name="numLocations" type="number" value={formData.numLocations} onChange={handleChange} />
                                        <TextField margin="normal" fullWidth label="Estimated weekly produce volume" placeholder="e.g., 50 boxes, 200 lbs" name="estimatedVolume" value={formData.estimatedVolume} onChange={handleChange} />
                                        <TextField margin="normal" fullWidth multiline rows={2} label="Preferred delivery days and times" name="preferredDeliveryTimes" value={formData.preferredDeliveryTimes} onChange={handleChange} />
                                        <FormControl component="fieldset" margin="normal">
                                            <FormLabel component="legend">Is there a loading dock or designated delivery access point?</FormLabel>
                                            <RadioGroup row name="hasLoadingDock" value={formData.hasLoadingDock} onChange={handleChange}>
                                                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                                                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                                            </RadioGroup>
                                        </FormControl>
                                        <TextField margin="normal" fullWidth multiline rows={3} label="Any special delivery instructions?" name="specialDeliveryInstructions" value={formData.specialDeliveryInstructions} onChange={handleChange} />
                                    </Step>

                                    <Step>
                                        <Typography variant="h5" gutterBottom>Service Expectations</Typography>
                                        <FormControl component="fieldset" margin="normal" fullWidth>
                                            <FormLabel component="legend">What are your priorities when choosing a supplier?</FormLabel>
                                            {/* CHANGE 2: Added flexWrap to the FormGroup to prevent overflow on smaller screens. */}
                                            <FormGroup row sx={{ flexWrap: 'wrap' }}>
                                                <FormControlLabel control={<Checkbox checked={formData.priorities.pricing} onChange={handleCheckboxChange} name="pricing" color="primary" />} label="Pricing" />
                                                <FormControlLabel control={<Checkbox checked={formData.priorities.freshness} onChange={handleCheckboxChange} name="freshness" color="primary" />} label="Freshness" />
                                                <FormControlLabel control={<Checkbox checked={formData.priorities.reliability} onChange={handleCheckboxChange} name="reliability" color="primary" />} label="Reliability" />
                                                <FormControlLabel control={<Checkbox checked={formData.priorities.variety} onChange={handleCheckboxChange} name="variety" color="primary" />} label="Variety" />
                                                <FormControlLabel control={<Checkbox checked={formData.priorities.customerService} onChange={handleCheckboxChange} name="customerService" color="primary" />} label="Customer Service" />
                                            </FormGroup>
                                        </FormControl>
                                        <TextField margin="normal" fullWidth multiline rows={2} label="Do you currently work with other wholesalers? If yes, why are you exploring new options?" name="reasonForSwitch" value={formData.reasonForSwitch} onChange={handleChange} />
                                    </Step>
                                    <Step>
                                        <Typography variant="h5" gutterBottom>Billing & Documentation</Typography>
                                        <FormControl component="fieldset" margin="normal" fullWidth>
                                            <FormLabel component="legend">Preferred Payment Method</FormLabel>
                                            <RadioGroup name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                                                <FormControlLabel value="cod" control={<Radio color="primary" />} label="Cash on Delivery" />
                                                <FormControlLabel value="credit_debit_online" control={<Radio color="primary" />} label="Credit Card / Debit (Online Secured Payment)" />
                                                <FormControlLabel value="e_transfer" control={<Radio color="primary" />} label="E-Transfer (We will provide the email where to send the money to)" />
                                                <FormControlLabel value="cheque" control={<Radio color="primary" />} label="Cheque (We will arrange pickup/delivery method for that)" />
                                            </RadioGroup>
                                        </FormControl>

                                        <FormControl component="fieldset" margin="normal" fullWidth sx={{ mt: 3 }}>
                                            <FormLabel component="legend">Statement Duration (How often to send statements)</FormLabel>
                                            <RadioGroup row name="statement_duration" value={formData.statement_duration} onChange={handleChange}>
                                                <FormControlLabel value="Daily" control={<Radio color="primary" />} label="Daily" />
                                                <FormControlLabel value="Weekly" control={<Radio color="primary" />} label="Weekly" />
                                                <FormControlLabel value="Semi-Monthly" control={<Radio color="primary" />} label="Semi-Monthly" />
                                                <FormControlLabel value="Monthly" control={<Radio color="primary" />} label="Monthly" />
                                            </RadioGroup>
                                        </FormControl>
                                    </Step>
                                    <Step>
                                        <Typography variant="h5" gutterBottom>✅ Final Notes</Typography>
                                        <TextField margin="normal" fullWidth multiline rows={5} label="Is there anything else we should know to better serve your business?" name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} />
                                        <Typography color="text.secondary" sx={{ mt: 2 }}>Click "Submit Application" to finalize your registration.</Typography>
                                    </Step>
                                </Stepper>
                            )}

                            <Typography align="center" sx={{ mt: 4 }}>
                                Already have an account?{' '}
                                <Link component={RouterLink} to="/login" variant="body2" color="primary">
                                    Sign In
                                </Link>
                            </Typography>
                        </Paper>
                    </Container>
                </PageAnimation>
            </ThemeProvider>
        );
    }


    return (
        <ThemeProvider theme={theme}>
            <PageAnimation>
                {/* CHANGE 1: Increased container width from 'md' to 'lg' to give the stepper more space. */}
                <Container maxWidth="lg">
                    <Paper elevation={6} sx={{ mt: 8, p: 4, borderRadius: 2 }}>
                        <Typography component="h1" variant="h4" align="center" gutterBottom>
                            📝 Matax Express Client Onboarding
                        </Typography>
                        <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
                            Complete the steps below to apply for your wholesale account.
                        </Typography>

                        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 5 }}>
                                <CircularProgress color="primary" />
                                <Typography variant="h6" sx={{ ml: 2 }}>Submitting your application...</Typography>
                            </Box>
                        ) : (
                            <Stepper
                                onFinalStepCompleted={handleFinalSubmit}
                                backButtonText="Back"
                                nextButtonText="Next"
                                finalStepButtonText="Submit Application"
                            >
                                <Step>
                                    <Typography variant="h5" gutterBottom>Company Information</Typography>
                                    <TextField margin="normal" required fullWidth label="Business Name" name="businessName" value={formData.businessName} onChange={handleChange} autoFocus />
                                    <TextField margin="normal" required fullWidth label="Your Name" name="contactPerson" value={formData.contactPerson} onChange={handleChange} />
                                    <TextField margin="normal" required fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} />
                                    <TextField margin="normal" required fullWidth label="Create a Password" name="password" type="password" value={formData.password} onChange={handleChange} />
                                </Step>
                                <Step>
                                    <Typography variant="h5" gutterBottom>More Information</Typography>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="Phone Number (e.g. +1234567890)"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        error={!!phoneError}
                                        helperText={phoneError}
                                    />
                                    <TextField margin="normal" fullWidth label="Company Website (Optional)" name="companyWebsite" value={formData.companyWebsite} onChange={handleChange} />
                                    <TextField margin="normal" fullWidth multiline rows={2} label="Business Address" name="businessAddress" value={formData.businessAddress} onChange={handleChange} />
                                </Step>
                                <Step>
                                    <Typography variant="h5" gutterBottom>Business Type & Operations</Typography>
                                    <TextField margin="normal" fullWidth label="What type of business are you?" placeholder="e.g., Restaurant, Catering Company, Grocer" name="businessType" value={formData.businessType} onChange={handleChange} />
                                    <TextField margin="normal" required fullWidth label="How long has your business been operating?" name="yearsOperating" value={formData.yearsOperating} onChange={handleChange} />
                                    <TextField margin="normal" fullWidth label="Number of locations" name="numLocations" type="number" value={formData.numLocations} onChange={handleChange} />
                                    {/* <TextField margin="normal" fullWidth label="Estimated weekly produce volume" placeholder="e.g., 50 boxes, 200 lbs" name="estimatedVolume" value={formData.estimatedVolume} onChange={handleChange} /> */}
                                </Step>
                                <Step>
                                    <Typography variant="h5" gutterBottom>Delivery & Logistics</Typography>
                                    <TextField margin="normal" fullWidth multiline rows={2} label="Preferred delivery days" name="preferredDeliveryTimes" value={formData.preferredDeliveryTimes} onChange={handleChange} />
                                    <FormControl component="fieldset" margin="normal">
                                        <FormLabel component="legend">Is there a loading dock or designated delivery access point?</FormLabel>
                                        <RadioGroup row name="hasLoadingDock" value={formData.hasLoadingDock} onChange={handleChange}>
                                            <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                                            <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                                        </RadioGroup>
                                    </FormControl>
                                    <TextField margin="normal" fullWidth multiline rows={3} label="Any special delivery instructions?" name="specialDeliveryInstructions" value={formData.specialDeliveryInstructions} onChange={handleChange} />
                                </Step>
                                <Step>
                                    <Typography variant="h5" gutterBottom>Service Expectations</Typography>
                                    <FormControl component="fieldset" margin="normal" fullWidth>
                                        <FormLabel component="legend">What are your priorities when choosing a supplier?</FormLabel>
                                        {/* CHANGE 2: Added flexWrap to the FormGroup to prevent overflow on smaller screens. */}
                                        <FormGroup row sx={{ flexWrap: 'wrap' }}>
                                            <FormControlLabel control={<Checkbox checked={formData.priorities.pricing} onChange={handleCheckboxChange} name="pricing" color="primary" />} label="Pricing" />
                                            <FormControlLabel control={<Checkbox checked={formData.priorities.freshness} onChange={handleCheckboxChange} name="freshness" color="primary" />} label="Freshness" />
                                            <FormControlLabel control={<Checkbox checked={formData.priorities.reliability} onChange={handleCheckboxChange} name="reliability" color="primary" />} label="Reliability" />
                                            <FormControlLabel control={<Checkbox checked={formData.priorities.variety} onChange={handleCheckboxChange} name="variety" color="primary" />} label="Variety" />
                                            <FormControlLabel control={<Checkbox checked={formData.priorities.customerService} onChange={handleCheckboxChange} name="customerService" color="primary" />} label="Customer Service" />
                                        </FormGroup>
                                    </FormControl>
                                    <TextField margin="normal" fullWidth multiline rows={2} label="Do you currently work with other wholesalers? If yes, why are you exploring new options?" name="reasonForSwitch" value={formData.reasonForSwitch} onChange={handleChange} />
                                </Step>
                                <Step>
                                    <Typography variant="h5" gutterBottom>Billing & Documentation</Typography>
                                    <FormControl component="fieldset" margin="normal" fullWidth>
                                        <FormLabel component="legend">Preferred Payment Method</FormLabel>
                                        <RadioGroup name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}>
                                            <FormControlLabel value="cod" control={<Radio color="primary" />} label="Cash on Delivery" />
                                            <FormControlLabel value="credit_debit_online" control={<Radio color="primary" />} label="Credit Card / Debit (Online Secured Payment)" />
                                            <FormControlLabel value="e_transfer" control={<Radio color="primary" />} label="E-Transfer (We will provide the email where to send the money to)" />
                                            <FormControlLabel value="cheque" control={<Radio color="primary" />} label="Cheque (We will arrange pickup/delivery method for that)" />
                                        </RadioGroup>
                                    </FormControl>

                                    <FormControl component="fieldset" margin="normal" fullWidth sx={{ mt: 3 }}>
                                        <FormLabel component="legend">Statement Duration (How often to send statements)</FormLabel>
                                        <RadioGroup row name="statement_duration" value={formData.statement_duration} onChange={handleChange}>
                                            <FormControlLabel value="Daily" control={<Radio color="primary" />} label="Daily" />
                                            <FormControlLabel value="Weekly" control={<Radio color="primary" />} label="Weekly" />
                                            <FormControlLabel value="Semi-Monthly" control={<Radio color="primary" />} label="Semi-Monthly" />
                                            <FormControlLabel value="Monthly" control={<Radio color="primary" />} label="Monthly" />
                                        </RadioGroup>
                                    </FormControl>
                                </Step>
                                <Step>
                                    <Typography variant="h5" gutterBottom>✅ Final Notes</Typography>
                                    <TextField margin="normal" fullWidth multiline rows={5} label="Is there anything else we should know to better serve your business?" name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} />
                                    <Typography color="text.secondary" sx={{ mt: 2 }}>Click "Submit Application" to finalize your registration.</Typography>
                                </Step>
                            </Stepper>
                        )}

                        <Typography align="center" sx={{ mt: 4 }}>
                            Already have an account?{' '}
                            <Link component={RouterLink} to="/login" variant="body2" color="primary">
                                Sign In
                            </Link>
                        </Typography>
                    </Paper>
                </Container>
            </PageAnimation>
        </ThemeProvider>
    );
}

export default RegisterPage;
